import { useCallback, useEffect, useRef } from "react";

import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react";

import { SUGGESTIONS_MENU_ATTR } from "@/components/suggestions/constants";
import { getIsMobile } from "@/utils/getIsMobile";
import { useScrollLock } from "@/utils/useScrollLock";

import { useWaitAfterScroll } from "./utils/useWaitAfterScroll";

import type { TiptapSuggestionsRenderPlace } from "@/components/tiptap/tiptap-suggestions/TiptapSuggestions";
import type { OffsetOptions } from "@floating-ui/react";

const MAX_REVEAL_HEIGHT = 125;
const SCROLL_MARGIN = 8;

export function useSuggestionsFloating({
  open,
  context = "math",
  place = "tiptap",
  pointerAnchor = null,
  onDismiss,
  options = {},
}: {
  open: boolean;
  context?: "text" | "math";
  place?: TiptapSuggestionsRenderPlace;
  pointerAnchor?: { x: number; y: number } | null;
  onDismiss?: () => void;
  options?: {
    offsetOptions?: OffsetOptions;
  };
}) {
  const isMobile = getIsMobile();
  const anchored = pointerAnchor !== null;
  const scrolledRef = useRef(false);
  const lastElRef = useRef<Element | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const cancelPendingScroll = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      cancelPendingScroll();
    }

    return cancelPendingScroll;
  }, [open, cancelPendingScroll]);

  useScrollLock(open && anchored);

  const waitAfterScroll = useWaitAfterScroll(globalThis.window);

  const result = useFloating({
    whileElementsMounted: autoUpdate,
    placement: isMobile ? "bottom" : "bottom-start",
    strategy: isMobile || anchored ? "fixed" : "absolute",
    open,
    middleware: [
      offset(
        anchored
          ? { mainAxis: 4 }
          : {
              mainAxis: 10,
              crossAxis: context === "math" ? -15 : -30,
              ...(typeof options?.offsetOptions === "object" ? options.offsetOptions : {}),
            },
      ),
      ...(anchored ? [flip({ padding: 8 }), shift({ padding: 8 })] : []),
      size({
        apply({ elements, availableHeight }) {
          const floatingEl = elements.floating;

          if (anchored) {
            floatingEl.style.maxHeight = `${availableHeight}px`;
            return;
          }

          if (isMobile || place !== "tiptap" || !floatingEl) {
            return;
          }

          if (floatingEl !== lastElRef.current) {
            lastElRef.current = floatingEl;
            scrolledRef.current = false;
          }

          if (floatingEl.scrollHeight === 0) {
            cancelPendingScroll();
            scrolledRef.current = false;
            return;
          }

          if (scrolledRef.current) return;
          scrolledRef.current = true;

          rafIdRef.current = requestAnimationFrame(async () => {
            rafIdRef.current = null;

            const waiter = waitAfterScroll();
            if (waiter) await waiter;

            const rect = floatingEl.getBoundingClientRect();
            const revealHeight = Math.min(rect.height, MAX_REVEAL_HEIGHT);
            const overflow = rect.top + revealHeight - window.innerHeight;

            if (overflow > 1) {
              window.scrollBy({ top: overflow + SCROLL_MARGIN });
            }
          });
        },
      }),
    ],
  });

  const { refs } = result;

  useEffect(() => {
    if (!open || !anchored || !onDismiss) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const floatingEl = refs.floating.current;
      if (floatingEl && event.target instanceof Node && floatingEl.contains(event.target)) {
        return;
      }
      // Nested submenus render in their own FloatingPortal (outside floatingEl), so
      // keep the popup open when the press lands inside any suggestions menu.
      if (event.target instanceof Element && event.target.closest(`[${SUGGESTIONS_MENU_ATTR}]`)) {
        return;
      }
      onDismiss();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [open, anchored, onDismiss, refs]);

  useEffect(() => {
    if (pointerAnchor) {
      const { x, y } = pointerAnchor;
      refs.setPositionReference({
        getBoundingClientRect: () => ({
          x,
          y,
          top: y,
          left: x,
          right: x,
          bottom: y,
          width: 0,
          height: 0,
          toJSON: () => ({}),
        }),
      });
    } else {
      refs.setPositionReference(null);
      refs.setReference(refs.domReference.current);
    }
  }, [pointerAnchor, refs]);

  return result;
}
